import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a date string (YYYY-MM-DD) as a local date to avoid timezone shift
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Date} - Date object in local timezone
 */
export function parseLocalDate(dateString) {
  if (!dateString) return null;
  // Handle YYYY-MM-DD format
  if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const parts = dateString.split('-');
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  }
  // Fallback for other formats (timestamps, ISO strings, etc.)
  return new Date(dateString);
}

export function formatDate(dateString) {
  if (!dateString) return '';
  const date = parseLocalDate(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function formatTime(timeString) {
  if (!timeString) return '';
  return timeString;
}

// In-memory cache populated once after login. Keyed by status name (lowercased).
// Pages that already fetch /job-statuses populate this via setStatusColorMap below.
let _statusColorMap = {};

export function setStatusColorMap(statuses) {
  if (!Array.isArray(statuses)) return;
  const next = {};
  for (const s of statuses) {
    if (s && s.name) next[s.name.toLowerCase()] = s.color || null;
  }
  _statusColorMap = next;
}

export function getStatusColor(status) {
  // Legacy hardcoded fallback used as className. Inline color override is applied
  // separately via getStatusStyle(status) at call sites that want admin-configured colors.
  const colors = {
    'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Scheduled': 'bg-blue-100 text-blue-800 border-blue-200',
    'In Progress': 'bg-amber-100 text-amber-800 border-amber-200',
    'Completed': 'bg-red-100 text-red-800 border-red-300',
    'Cancelled': 'bg-slate-100 text-slate-600 border-slate-200'
  };
  return colors[status] || 'bg-slate-100 text-slate-600';
}

/**
 * Returns an inline-style object for a status badge using the admin-configured color.
 * Returns null when there's no custom color (caller should use the className from getStatusColor).
 */
export function getStatusStyle(status) {
  const custom = status && _statusColorMap[String(status).toLowerCase()];
  if (!custom) return null;
  return {
    backgroundColor: custom,
    color: getContrastTextColor(custom),
    borderColor: 'rgba(0,0,0,0.08)'
  };
}

export function getPriorityColor(priority) {
  const colors = {
    'Low': 'bg-slate-100 text-slate-600 border-slate-200',
    'Normal': 'bg-blue-100 text-blue-700 border-blue-200',
    'High': 'bg-orange-100 text-orange-700 border-orange-200',
    'Urgent': 'bg-red-100 text-red-700 border-red-200'
  };
  return colors[priority] || 'bg-slate-100 text-slate-600';
}

export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function downloadCSV(data, filename) {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }
  
  const headers = Object.keys(data[0] || {});
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(h => `"${(row[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Calculate contrast text color (white or dark) based on background color brightness
 * @param {string} hexColor - Hex color code (with or without #)
 * @returns {string} - '#1e293b' (dark) for light backgrounds, 'white' for dark backgrounds
 */
export function getContrastTextColor(hexColor) {
  if (!hexColor) return 'white';
  // Remove # if present
  const hex = hexColor.replace('#', '');
  // Handle short hex codes
  const fullHex = hex.length === 3 
    ? hex.split('').map(c => c + c).join('') 
    : hex;
  // Convert to RGB
  const r = parseInt(fullHex.substr(0, 2), 16);
  const g = parseInt(fullHex.substr(2, 2), 16);
  const b = parseInt(fullHex.substr(4, 2), 16);
  // Calculate luminance (perceived brightness)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // Return dark text for light colors, white for dark colors
  return luminance > 0.5 ? '#1e293b' : 'white';
}
